import { c as _c } from "react/compiler-runtime";
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormGroup } from 'record-it-components/form/FormGroup/FormGroup';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { useModalOpened } from 'record-it-components/hooks/useModalOpened/useModalOpened';
import { BufferedTextInput, TextInputType } from 'record-it-components/input/TextInput/TextInput';
import { LoadingOverlay } from 'record-it-components/misc/LoadingOverlay/LoadingOverlay';
import { MessageBox, MessageBoxType } from 'record-it-components/misc/MessageBox/MessageBox';
import { SingleTabEditDialog, SingleTabEditDialogProps } from 'record-it-components/modal/SingleTabEditDialog/SingleTabEditDialog';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { ApiRequester } from '../../../classes/ApiRequester/ApiRequester';
import { useLoginFunction } from '../../../hooks/authentication/useLoginFunction/useLoginFunction';
import { useAuth } from '../../../hooks/useAuth';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
export type AutoReauthenticateDialogProps = PropsWithoutChildren<Omit<SingleTabEditDialogProps, 'tabTitle' | 'onAccept' | 'opened'>>;
export const AutoReauthenticateDialog: FunctionComponent<AutoReauthenticateDialogProps> = React.memo(props => {
  const $ = _c(39);
  let t0;
  if ($[0] !== props) {
    t0 = prepareProps({
      props
    });
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let remainingProps;
  if ($[2] !== t0) {
    ({
      ...remainingProps
    } = t0);
    $[2] = t0;
    $[3] = remainingProps;
  } else {
    remainingProps = $[3];
  }
  let t1;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      closeOnBackDropClick: false
    };
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const {
    setOpened,
    modalProps
  } = useModalOpened(t1);
  const {
    t
  } = useTranslation();
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState();
  const {
    authenticationData
  } = useAuth();
  const [loading, setLoading] = useState(false);
  let t2;
  if ($[5] !== setOpened) {
    t2 = () => {
      setOpened(false);
    };
    $[5] = setOpened;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== authenticationData || $[8] !== setOpened) {
    t3 = [authenticationData, setOpened];
    $[7] = authenticationData;
    $[8] = setOpened;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[10] !== setOpened) {
    t4 = () => {
      const {
        dispose
      } = ApiRequester.onAuthenticationError(() => {
        setOpened(true);
      });
      return () => {
        dispose();
      };
    };
    t5 = [setOpened];
    $[10] = setOpened;
    $[11] = t4;
    $[12] = t5;
  } else {
    t4 = $[11];
    t5 = $[12];
  }
  useEffect(t4, t5);
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = pass => {
      setPassword(pass);
    };
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const handlePasswordInput = useEventCallback(t6);
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      onError: errorResponse => {
        setErrorMessage(errorResponse.message);
      }
    };
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const login = useLoginFunction(t7);
  const currentUser = useCurrentUser();
  let t8;
  if ($[15] !== currentUser || $[16] !== login || $[17] !== password) {
    t8 = () => {
      setLoading(true);
      login(currentUser.getRequiredCurrentUser().email, password).finally(() => {
        setLoading(false);
      });
    };
    $[15] = currentUser;
    $[16] = login;
    $[17] = password;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const handleAccept = useEventCallback(t8);
  let t9;
  if ($[19] !== t) {
    t9 = t("components.authentication.AutoReauthenticateDialog.title");
    $[19] = t;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== errorMessage) {
    t10 = errorMessage ? <MessageBox type={MessageBoxType.Error}>{errorMessage}</MessageBox> : null;
    $[21] = errorMessage;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t) {
    t11 = t("entities.User.password");
    $[23] = t;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== handlePasswordInput || $[26] !== password || $[27] !== t11) {
    t12 = <FormGroup><BufferedTextInput label={t11} type={TextInputType.Password} value={password} onInput={handlePasswordInput} data-testid="AutoReauthenticateDialog.PasswordInput" /></FormGroup>;
    $[25] = handlePasswordInput;
    $[26] = password;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== loading) {
    t13 = <LoadingOverlay active={loading} />;
    $[29] = loading;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== handleAccept || $[32] !== modalProps || $[33] !== remainingProps || $[34] !== t10 || $[35] !== t12 || $[36] !== t13 || $[37] !== t9) {
    t14 = <SingleTabEditDialog {...remainingProps} {...modalProps} tabTitle={t9} onAccept={handleAccept}>{t10}{t12}{t13}</SingleTabEditDialog>;
    $[31] = handleAccept;
    $[32] = modalProps;
    $[33] = remainingProps;
    $[34] = t10;
    $[35] = t12;
    $[36] = t13;
    $[37] = t9;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  return t14;
});